<template>
  <div
    id="new-intruction-panel"
    class="fixed w-screen flex justify-end transition-all duration-500"
    :style="{
      top: chevronOpened ? 'calc(4.5rem + 5.75rem)' : 'calc(100vh - 3.5rem)',
      right: open ? '0vw' : '-200vw',
      height: 'calc(100vh - 4.5rem - 5.75rem)',
      zIndex: 52
    }"
  >
    <div
      class="h-full w-screen absolute left-0 top-0 -z-10"
      @click="chevronOpened = false"
    ></div>

    <div
      class="h-full w-120 flex-shrink-0 flex flex-col bg-pure-white relative z-0"
    >
      <div
        class="px-6 py-4 flex items-center gap-4 rounded-t-3xl bg-light-purple-300 cursor-pointer"
        @click="chevronOpened = !chevronOpened"
      >
        <BlIcon
          v-if="showIcon"
          icon="chevron-down"
          color="secondary"
          class="w-5 h-5 transition-all duration-300"
          :style="{ rotate: !chevronOpened ? '180deg' : '' }"
        />
        <BlTextSubtitle2
          text="Create new instruction"
          class="text-pure-white"
        />
      </div>
      <div
        class="relative h-full flex flex-col justify-between overflow-y-auto"
      >
        <div class="py-5 px-6 flex flex-col gap-3">
          <div class="flex items-center gap-2">
            <div
              v-for="opt in typeOptions"
              :key="opt.value"
              class="h-7.5 px-2 flex items-center gap-2 rounded-full bg-lime-100 cursor-pointer border"
              :class="[
                opt.value === typeInput ? 'border-lime-600' : 'border-lime-100'
              ]"
              @click="typeInput = opt.value"
            >
              <BlIcon
                v-if="opt.value === 'notes'"
                icon="paper-note"
                color="lime-6"
                class="h-4 w-4"
              />
              <BlIcon
                v-if="opt.value === 'todo'"
                icon="to-do"
                color="lime-6"
                class="h-4 w-4"
              />
              <BlTextBody2 :text="opt.label" class="text-lime-700" />
            </div>
          </div>
          <BlInputText :input="titleInput" @changed="inputHandler" />
          <div v-if="chevronOpened" class="pb-px relative">
            <VueEditor
              :editor-toolbar="customToolbar"
              v-model="descriptionInput"
              class="blum-editor"
              placeholder="Description"
            />
          </div>
          <div
            v-if="uploads.length"
            class="p-2 flex flex-col gap-2 rounded bg-slate-200"
          >
            <BlFileHolder
              v-for="upload in uploads"
              :key="upload.lastModified"
              :name="upload.name"
              :size="upload.size"
              removeable
              @remove-handler="onRemoveDoc(upload)"
            />
          </div>
          <div class="flex items-center justify-between">
            <input
              type="file"
              :name="`new_upload_document_prospect_${prospectStage.id}`"
              :id="`new_upload_document_prospect_${prospectStage.id}`"
              @change="onAddDoc"
              hidden
            />
            <BlInputButton
              v-if="chevronOpened"
              type="plain"
              dense
              icon="plus"
              label="Add document"
              @clicked="openUpload"
            />
            <BlTextBody2
              v-if="uploads.length"
              :text="`${uploads.length} file(s) added`"
            />
          </div>
          <div
            v-if="typeInput === 'todo'"
            class="grid grid-cols-2 gap-y-3 gap-x-2"
            :class="{ 'pt-8': !uploads.length }"
          >
            <BlSelectBox
              v-if="chevronOpened"
              class="col-span-2"
              search
              placeholder="Assign to (optional)"
              v-model="assignedUserInput"
              :fetchProperty="{
                url: `/api/v1/users?sort=name_asc&status=active&role=admin&query=`,
                dataKey: 'users',
                countKey: 'total_users',
                optionKeys: { value: 'id', label: 'name' }
              }"
              @change="assignedUserInput = $event"
              drop="up"
            />
            <BlInputDate
              v-if="chevronOpened"
              :input="dueDateInput"
              @changed="inputHandler"
            />
            <BlInputTime
              :input="dueDateTimeInput"
              @changed="inputHandler"
              :icon="false"
            />
          </div>
        </div>
        <div
          class="h-18 px-6 pt-4 sticky bottom-0 flex-shrink-0 bg-pure-white grid"
        >
          <BlInputButton
            label="Create"
            :disabled="isDisabledSubmit"
            :loading="submitLoading"
            @clicked="onCreate"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { VueEditor } from "vue2-editor";

import BlIcon from "../../../BlIcon.vue";
import BlTextTitle4 from "../../../BlTextTitle4.vue";
import BlTextSubtitle2 from "../../../BlTextSubtitle2.vue";
import BlTextBody2 from "../../../BlTextBody2.vue";
import BlInputText from "../../../BlInputText.vue";
import BlInputDate from "../../../BlInputDate.vue";
import BlInputTime from "../../../BlInputTime.vue";
import BlSelectBox from "../../../BlSelectBox.vue";
import BlFileHolder from "../../../BlFileHolder.vue";
import BlInputButton from "../../../BlInputButton.vue";

export default {
  name: "NewInstructionProspectPanel",
  components: {
    VueEditor,
    BlIcon,
    BlTextTitle4,
    BlTextSubtitle2,
    BlTextBody2,
    BlInputText,
    BlInputDate,
    BlInputTime,
    BlSelectBox,
    BlFileHolder,
    BlInputButton
  },
  data() {
    return {
      open: false,
      showIcon: false,
      //
      submitLoading: false,
      chevronOpened: false,
      typeInput: "notes", // notes | todo
      typeOptions: [
        { value: "notes", label: "Notes" },
        { value: "todo", label: "To-do" }
      ],
      titleInput: {
        placeholder: "Title",
        model: ""
      },
      descriptionInput: "",
      customToolbar: [["bold", "italic", "underline", "link"]],
      uploads: [],
      files: [],
      assignedUserInput: null,
      dueDateInput: {
        model: ""
      },
      dueDateTimeInput: {
        format: ["h", "m"],
        model: ""
      }
    };
  },
  computed: {
    ...mapGetters(["prospectStage"]),
    isDisabledSubmit() {
      if (!this.titleInput.model) return true;
      return false;
    }
  },
  watch: {
    chevronOpened(val) {
      if (val) {
        this.typeInput = "notes";
        this.titleInput.model = "";
        this.descriptionInput = "";
        this.uploads = [];
        this.files = [];
        this.assignedUserInput = null;
        this.dueDateInput.model = "";
        this.dueDateTimeInput.model = "";
      }
    }
  },
  mounted() {
    this.open = false;
    this.showIcon = false;
    setTimeout(() => {
      this.showIcon = true;
      this.open = true;
    }, 300);
  },
  methods: {
    ...mapActions(["createInstruction"]),
    inputHandler(payload) {
      payload.input.model = payload.value;
    },
    openUpload() {
      const input = document.getElementById(
        `new_upload_document_prospect_${this.prospectStage.id}`
      );
      if (input) input.click();
    },
    byteConverter(bytes, decimals, only) {
      const K_UNIT = 1024;
      const SIZES = ["Bytes", "KB", "MB", "GB", "TB", "PB"];

      if (bytes === 0) return "0 Byte";

      if (only === "MB") {
        return (bytes / (K_UNIT * K_UNIT)).toFixed(decimals) + " MB";
      }

      let i = Math.floor(Math.log(bytes) / Math.log(K_UNIT));
      let resp =
        parseFloat((bytes / Math.pow(K_UNIT, i)).toFixed(decimals)) +
        " " +
        SIZES[i];

      return resp;
    },
    onAddDoc(e) {
      const files = e.target.files;
      if (files && files.length) {
        const upload = {
          lastModified: files[0].lastModified,
          name: files[0].name,
          size: files[0].size,
          sizeString: this.byteConverter(files[0].size, 2)
        };
        this.files = [...this.files, files[0]];
        this.uploads = [...this.uploads, upload];
      }
    },
    onRemoveDoc(upload) {
      const index = this.uploads.findIndex(
        i => i.lastModified === upload.lastModified
      );
      if (index > -1) {
        this.files = this.files.filter(
          i => i.lastModified !== upload.lastModified
        );
        this.uploads = this.uploads.filter(
          i => i.lastModified !== upload.lastModified
        );
      }
    },
    onCreate() {
      const formData = new FormData();
      formData.append("instruction[instructionable_id]", this.prospectStage.id);
      formData.append("instruction[instructionable_type]", "ProspectStage");
      formData.append("instruction[instruction_type]", this.typeInput);
      formData.append("instruction[title]", this.titleInput.model);
      formData.append("instruction[description]", this.descriptionInput);
      this.files.forEach((file, idx) => {
        formData.append(`instruction[documents][${idx}]`, file);
      });
      const jsonData = {
        instruction: {
          instructionable_id: this.prospectStage.id,
          instructionable_type: "ProspectStage",
          instruction_type: this.typeInput,
          title: this.titleInput.model,
          description: this.descriptionInput,
          documents: []
        }
      };
      if (this.typeInput === "todo") {
        formData.append(
          "instruction[assigned_user_id]",
          this.assignedUserInput
        );
        jsonData.instruction["assigned_user_id"] = this.assignedUserInput;
        if (this.dueDateInput.model) {
          const time = this.dueDateTimeInput.model || "00:00";
          const dueDate = `${this.dueDateInput.model}T${time}:00`;
          formData.append("instruction[due_date]", dueDate);
          jsonData.instruction["due_date"] = dueDate;
        }
      }
      const data = this.files.length ? formData : jsonData;
      this.submitLoading = true;
      this.createInstruction(data).then(({ success }) => {
        this.submitLoading = false;
        if (!success) return;
        this.chevronOpened = false;
      });
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/salesPipeline/modal/prospectPanel/NewInstructionProspectPanel.vue